import { DfeInventoryEntry } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Package, Hash, ArrowRightLeft, Trash2, AlertTriangle } from 'lucide-react';
import { isSafeImageUri } from '../utils';

interface InventoryItemCardProps {
  item: DfeInventoryEntry;
  hasConflict?: boolean;
  onTransfer?: (item: DfeInventoryEntry) => void;
  onDelete?: (item: DfeInventoryEntry) => void;
  onClick?: (item: DfeInventoryEntry) => void;
}

export function InventoryItemCard({
  item,
  hasConflict,
  onTransfer,
  onDelete,
  onClick,
}: InventoryItemCardProps) {
  return (
    <Card
      className={`overflow-hidden transition-shadow hover:shadow-md ${onClick ? 'cursor-pointer' : ''}`}
      onClick={() => onClick?.(item)}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(item);
              }
            }
          : undefined
      }
    >
      <div className="aspect-square bg-muted flex items-center justify-center">
        {isSafeImageUri(item.thumbnailUri) ? (
          <img src={item.thumbnailUri!} alt={item.name} className="w-full h-full object-cover" />
        ) : (
          <Package className="h-12 w-12 text-muted-foreground" />
        )}
      </div>
      <CardContent className="p-3">
        <h3 className="font-medium text-sm truncate">{item.name}</h3>
        <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
          {item.fungibility === 'fungible' ? (
            <span>Qty: {item.quantity}</span>
          ) : (
            <span className="flex items-center gap-0.5">
              <Hash className="h-3 w-3" />
              {item.instanceNumber}
            </span>
          )}
          {hasConflict && (
            <AlertTriangle
              className="h-3 w-3 text-yellow-500 ml-1"
              aria-label="Position conflict"
            />
          )}
        </div>
        {(item.category || item.position) && (
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {item.category && <span>{item.category}</span>}
            {item.category && item.position && <span>:</span>}
            {item.position && <span>{item.position}</span>}
          </div>
        )}
        <div className="flex gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
          {onTransfer && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-7 text-xs"
              onClick={() => onTransfer(item)}
              aria-label={`Transfer ${item.name}`}
            >
              <ArrowRightLeft className="h-3 w-3 mr-1" />
              Transfer
            </Button>
          )}
          {onDelete && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs text-destructive"
              onClick={() => onDelete(item)}
              aria-label={`Delete ${item.name}`}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
